import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Bell, Shield, Globe, Smartphone, Mail, Lock } from 'lucide-react';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { userSettingsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const Settings = () => {
  const { changePassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    payment_reminders: true,
    club_updates: true,
    language: 'en',
    timezone: 'Africa/Dar_es_Salaam',
    theme: 'light',
    auto_payment: true,
    payment_phone: '+255621728109'
  });

  const [security, setSecurity] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_factor_enabled: false
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsFetching(true);
        const fetchedSettings = await userSettingsApi.getSettings();
        setSettings(prev => ({ ...prev, ...fetchedSettings }));
      } catch (error) {
        toast({
          title: "Failed to load settings",
          description: "Could not retrieve your current settings from the server.",
          variant: "destructive",
        });
      } finally {
        setIsFetching(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSettingChange = (key: string, value: boolean | string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSecurityChange = (key: string, value: boolean | string) => {
    setSecurity(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      await userSettingsApi.updateSettings(settings);
      toast({
        title: "Settings saved",
        description: "Your preferences have been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Save failed",
        description: error.response?.data?.message || "There was an error saving your settings.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (security.new_password !== security.confirm_password) {
      toast({
        title: "Password mismatch",
        description: "New passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(security.current_password, security.new_password, security.confirm_password);
      toast({
        title: "Password changed",
        description: "Your password has been updated successfully.",
      });
      setSecurity(prev => ({ 
        ...prev, 
        current_password: '', 
        new_password: '', 
        confirm_password: '' 
      }));
    } catch (error: any) {
      toast({
        title: "Password change failed",
        description: error.response?.data?.message || "There was an error changing your password.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your account preferences and security settings
          </p>
        </div>

        <div className="space-y-8">
          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5" />
                <span>Notifications</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Enable Notifications</Label>
                  <p className="text-sm text-gray-600">Receive notifications about your account</p>
                </div>
                <Switch
                  checked={settings.notifications_enabled}
                  onCheckedChange={(checked) => handleSettingChange('notifications_enabled', checked)}
                />
              </div>

              <div className="pl-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Email Notifications</Label>
                    <p className="text-sm text-gray-600">Get notified via email</p>
                  </div>
                  <Switch
                    checked={settings.email_notifications}
                    onCheckedChange={(checked) => handleSettingChange('email_notifications', checked)}
                    disabled={!settings.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">SMS Notifications</Label>
                    <p className="text-sm text-gray-600">Get notified via SMS</p>
                  </div>
                  <Switch
                    checked={settings.sms_notifications}
                    onCheckedChange={(checked) => handleSettingChange('sms_notifications', checked)}
                    disabled={!settings.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Payment Reminders</Label>
                    <p className="text-sm text-gray-600">Reminders for upcoming payments</p>
                  </div>
                  <Switch
                    checked={settings.payment_reminders}
                    onCheckedChange={(checked) => handleSettingChange('payment_reminders', checked)}
                    disabled={!settings.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Club Updates</Label>
                    <p className="text-sm text-gray-600">News and updates from the club</p>
                  </div>
                  <Switch
                    checked={settings.club_updates}
                    onCheckedChange={(checked) => handleSettingChange('club_updates', checked)}
                    disabled={!settings.notifications_enabled}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="h-5 w-5" />
                <span>Preferences</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select onValueChange={(value) => handleSettingChange('language', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="sw">Swahili</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select onValueChange={(value) => handleSettingChange('timezone', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Africa/Dar_es_Salaam">East Africa Time (EAT)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Smartphone className="h-5 w-5" />
                <span>Payment Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Auto Payment</Label>
                  <p className="text-sm text-gray-600">Automatically pay monthly fees</p>
                </div>
                <Switch
                  checked={settings.auto_payment}
                  onCheckedChange={(checked) => handleSettingChange('auto_payment', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_phone">Payment Phone Number</Label>
                <Input
                  id="payment_phone"
                  value={settings.payment_phone}
                  onChange={(e) => handleSettingChange('payment_phone', e.target.value)}
                  placeholder="+255621728109"
                />
                <p className="text-sm text-gray-600">
                  Phone number for mobile money payments
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <span>Security</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Two-Factor Authentication</Label>
                  <p className="text-sm text-gray-600">Add an extra layer of security</p>
                </div>
                <Switch
                  checked={security.two_factor_enabled}
                  onCheckedChange={(checked) => handleSecurityChange('two_factor_enabled', checked)}
                />
              </div>

              <div className="border-t pt-6">
                <h4 className="text-lg font-semibold mb-4">Change Password</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={security.current_password}
                      onChange={(e) => handleSecurityChange('current_password', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={security.new_password}
                      onChange={(e) => handleSecurityChange('new_password', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={security.confirm_password}
                      onChange={(e) => handleSecurityChange('confirm_password', e.target.value)}
                    />
                  </div>
                  <Button 
                    onClick={handleChangePassword}
                    disabled={!security.current_password || !security.new_password || !security.confirm_password}
                    className="bg-basketball-gradient hover:opacity-90"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveSettings}
              className="bg-basketball-gradient hover:opacity-90 px-8"
              disabled={isLoading}
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Save All Settings'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
